/**
 * Authority and Meridian grammars: Listening Sails / Authority, Span / Authority, Trunk Line / Meridian, Span / Meridian.
 * Authority: thick symmetric chevron plates, a stepped keep, mast crowns with climbing strobes; never asymmetric.
 * Meridian: countable rounded pod tiers with collar necks and many small lit collar rings, never one big ring.
 */
import { detailRng, gateQuadrant, lumClass, lumScale, part, radial, regionIndex, rf } from './kit.ts'
import type { FamilyGrammar, MegaPart, Vec3 } from './types.ts'

const TAU = Math.PI * 2

/** The Charter keep: plate, stepped keep tiers, buttresses. Returns the keep's top height and top width. */
function charterKeep(out: MegaPart[], H: number, tiers: number, plateI: number, profileI: number, plates: number, wf = 1): readonly [number, number] {
  const pw = 0.78 * H * wf
  const pt = 0.06 * H
  let y = 0
  for (let k = 0; k < plates; k++) {
    const w = pw * (1 - 0.12 * k)
    if (plateI === 0) {
      out.push(part('slab', [0, y + pt / 2, 0], [w, pt, w * 0.62], { taper: 0.9 }))
      out.push(part('slab', [w * 0.3, y + pt / 2, 0], [w * 0.36, pt, w * 0.36], { rot: [0, Math.PI / 4, 0] }))
      out.push(part('slab', [-w * 0.3, y + pt / 2, 0], [w * 0.36, pt, w * 0.36], { rot: [0, Math.PI / 4, 0] }))
    } else if (plateI === 1) {
      out.push(part('slab', [0, y + pt / 2, 0], [w * 0.8, pt, w * 0.8]))
      out.push(part('slab', [0, y + pt / 2, 0], [w * 0.8, pt, w * 0.8], { rot: [0, Math.PI / 4, 0] }))
    } else if (plateI === 2) {
      out.push(part('slab', [0, y + pt / 2, 0], [w, pt * 0.7, w * 0.5]))
      out.push(part('slab', [0, y + pt * 1.6, 0], [w * 0.5, pt * 0.7, w]))
    } else {
      out.push(part('frustum', [0, y + pt, 0], [w * 0.9, pt * 2, w * 0.9], { taper: 0.55 }))
    }
    y += pt * (plateI === 2 || plateI === 3 ? 2.2 : 1.1)
  }
  let kw = 0.42 * H * wf
  const th = 0.15 * H
  for (let i = 0; i < tiers; i++) {
    const w = profileI === 2 && i % 2 === 1 ? kw * 0.78 : kw
    const taper = profileI === 1 ? 0.84 : 1
    out.push(part('slab', [0, y + th / 2, 0], [w, th, w], { taper, step: i === 0 ? 1 : 2 }))
    if (profileI === 3 && i === 0) {
      for (let b = 0; b < 4; b++) {
        out.push(radial('blade', (b * TAU) / 4, w * 0.58, th * 0.7, [w * 0.3, th * 1.4, w * 0.16], { taper: 0.3, shear: -0.12, step: 2 }))
      }
    }
    y += th
    kw *= 0.74
  }
  return [y, kw / 0.74]
}

/** A mast crown with climbing strobes (accent): a slim frustum and a column of small beads. */
function mastCrown(out: MegaPart[], x: number, y: number, z: number, h: number, step: number): void {
  out.push(part('frustum', [x, y + h / 2, z], [h * 0.07, h, h * 0.07], { taper: 0.25, step, role: 'accent' }))
  for (let k = 1; k <= 3; k++) out.push(part('sphere', [x, y + (h * k) / 4, z], [h * 0.06, h * 0.03, h * 0.06], { step, role: 'accent' }))
}

// ── Listening Sails / Authority ────────────────────────────────────────────────────────────────────────────────────
// Round 2: a fat Charter keep whose crown flares into a broad sealed listening dish (no pedestal neck, no feed spike),
// chevron fins standing round the rim and mast crowns on it; the cradled option holds the dish between two Standards.
export const listeningSailsAuthority: FamilyGrammar = {
  kind: 'listeningSails',
  maker: 'authority',
  slots: [
    { name: 'tiers', options: 3 }, // 2/3/4 keep steps
    { name: 'plate', options: 4 }, // chevron, octagon, crossed, skirt
    { name: 'dishSize', options: 4, site: lumClass },
    { name: 'mount', options: 3 }, // flush crown skirt, raised collar crown, cradled between two Standards
    { name: 'masts', options: 4 }, // 1 centre, 2 flanking, 3, 4 corners (on the rim)
    { name: 'profile', options: 4 }, // stepped, battered, waisted, buttressed
    { name: 'plates', options: 4, site: regionIndex }, // stacked Charter plates: gate one → rim four
    { name: 'chevrons', options: 3 }, // 4/6/8 chevron fins on the rim
  ],
  build(g, site) {
    const [tierI, plateI, , mountI, mastI, profI, platesI, chevI] = g.slots
    const p = g.props
    const H = 2000 + 700 * p[0]
    const out: MegaPart[] = []
    const [top, kw] = charterKeep(out, H, tierI + 2, plateI, profI, 1 + platesI, 1.45)
    const gb = site.gateBearingRad
    const dd = 0.62 * H * (0.75 + 0.25 * lumScale(site))
    const depth = dd * 0.2
    // Crown collar (step 3): a sealed skirt flaring from the top tier out under the dish rim.
    let y = top
    if (mountI === 1) {
      out.push(part('drum', [0, y + 0.06 * H, 0], [kw * 0.82, 0.12 * H, kw * 0.82], { step: 3 }))
      y += 0.12 * H
    }
    const skirtH = 0.12 * H
    out.push(part('frustum', [0, y + skirtH / 2, 0], [kw * 0.95, skirtH, kw * 0.95], { taper: (dd * 0.78) / (kw * 0.95), step: 3 }))
    y += skirtH
    // The dish (step 4) sits in the crown, opening to the sky, with a sealed listening core.
    out.push(part('dish', [0, y + depth / 2, 0], [dd, depth, dd], { rot: [0, -gb, 0], step: 4, role: 'signature' }))
    out.push(part('frustum', [0, y + depth * 0.5, 0], [dd * 0.16, depth * 0.9, dd * 0.16], { taper: 0.55, step: 4, role: 'signature' }))
    out.push(part('sphere', [0, y + depth, 0], [dd * 0.1, dd * 0.07, dd * 0.1], { step: 4, role: 'signature' }))
    const rimY = y + depth
    // Chevron fins round the rim (step 5), squared to the gate line.
    const nC = [4, 6, 8][chevI]
    for (let k = 0; k < nC; k++) {
      const b = gb + (k / nC) * TAU
      out.push(radial('blade', b, dd * 0.5, rimY + 0.04 * H, [dd * 0.16, 0.14 * H, 0.035 * H], { rot: [0, 0, -0.35], taper: 0.25, step: 5, role: 'signature' }))
    }
    // Cradled mount: two knife-edged Standards stand either side of the dish on the gate line (step 3).
    if (mountI === 2) {
      for (const s of [0, Math.PI]) {
        out.push(radial('blade', gb + s + Math.PI / 2, dd * 0.56, top + 0.3 * H, [0.1 * H, 0.6 * H, 0.05 * H], { taper: 0.35, step: 3, role: 'signature' }))
      }
    }
    // Mast crowns (step 5), symmetric, standing on the rim.
    const mh = 0.3 * H * (0.85 + 0.3 * p[1])
    const ms: readonly (readonly [number, number])[] =
      mastI === 0 ? [[0, 0]] : mastI === 1 ? [[-1, 0], [1, 0]] : mastI === 2 ? [[0, -1], [-0.87, 0.5], [0.87, 0.5]] : [[-0.71, -0.71], [0.71, -0.71], [-0.71, 0.71], [0.71, 0.71]]
    for (const [mx, mz] of ms) {
      const r = mastI === 0 ? 0 : dd * 0.46
      const c = Math.cos(gb)
      const s = Math.sin(gb)
      mastCrown(out, (mx * c - mz * s) * r, mastI === 0 ? rimY : rimY - depth * 0.3, (mx * s + mz * c) * r, mastI === 0 ? mh * 0.8 : mh, 5)
    }
    return out
  },
}

// ── Span / Authority ───────────────────────────────────────────────────────────────────────────────────────────────
// Round 2: the keep leads (×1.6 width, more plates, flanking wing keeps); the emitter mast is shorter and reads as the accent.
export const spanAuthority: FamilyGrammar = {
  kind: 'span',
  maker: 'authority',
  slots: [
    { name: 'tiers', options: 3 }, // 2/3/4 keep steps
    { name: 'plate', options: 4 },
    { name: 'elevation', options: 4 }, // emitter mast at 0°, 15°, 30°, 45°
    { name: 'mastLen', options: 3 },
    { name: 'head', options: 4 }, // sphere, horn, twin prongs, dish
    { name: 'strobes', options: 4 }, // 4/6/8/10 beacons down the lane
    { name: 'aim', options: 4, site: gateQuadrant }, // the emitter fires down the lane toward the relay
    { name: 'plates', options: 4, site: regionIndex }, // stacked plates: gate one → rim four
  ],
  build(g, site) {
    const [tierI, plateI, elevI, lenI, headI, strI, , platesI] = g.slots
    const p = g.props
    const H = 2000 + 600 * p[0]
    const out: MegaPart[] = []
    const [top, kw] = charterKeep(out, H, tierI + 2, plateI, Math.floor(p[2] * 4) % 4, 1 + platesI, 1.6)
    const b = site.gateBearingRad
    const side: Vec3 = [-Math.sin(b), 0, Math.cos(b)]
    // Wing keeps (step 2): two symmetric stepped keeps either side of the lane axis.
    const wy = 0.06 * H * (1 + platesI)
    for (const s of [-1, 1]) {
      const c: Vec3 = [side[0] * s * 0.42 * H, 0, side[2] * s * 0.42 * H]
      out.push(part('slab', [c[0], wy + 0.14 * H, c[2]], [0.26 * H, 0.28 * H, 0.26 * H], { rot: [0, -b, 0], taper: 0.8, step: 2 }))
      out.push(part('slab', [c[0], wy + 0.34 * H, c[2]], [0.17 * H, 0.12 * H, 0.17 * H], { rot: [0, -b, 0], taper: 0.75, step: 2 }))
      mastCrown(out, c[0], wy + 0.4 * H, c[2], 0.22 * H, 5)
    }
    const el = [0, 0.4, 0.8, 1.2][elevI]
    const len = [0.42, 0.55, 0.68][lenI] * H
    const d: Vec3 = [Math.cos(b) * Math.cos(el), Math.sin(el), Math.sin(b) * Math.cos(el)]
    const mw = Math.max(kw * 0.42, 0.1 * H)
    const base: Vec3 = [0, top + mw * 0.4, 0]
    const rot: Vec3 = [0, -b, el - Math.PI / 2]
    const at = (t: number): Vec3 => [base[0] + d[0] * t, base[1] + d[1] * t, base[2] + d[2] * t]
    // The sealed emitter mast (steps 3–4): a mount block, then a tapering mast.
    out.push(part('slab', [0, top + mw * 0.35, 0], [mw * 1.6, mw * 0.7, mw * 1.6], { taper: 0.8, step: 3 }))
    out.push(part('frustum', at(len * 0.25), [mw, len * 0.5, mw], { rot, taper: 0.75, step: 3, role: 'signature' }))
    out.push(part('frustum', at(len * 0.75), [mw * 0.72, len * 0.5, mw * 0.72], { rot, taper: 0.6, step: 4, role: 'signature' }))
    // Emitter head (step 5).
    const tip = at(len)
    const hs = mw * 1.1
    if (headI === 0) out.push(part('sphere', tip, [hs, hs, hs], { step: 5, role: 'signature' }))
    else if (headI === 1) out.push(part('frustum', at(len + hs * 0.5), [hs * 0.5, hs, hs * 0.5], { rot, taper: 2.4, step: 5, role: 'signature' }))
    else if (headI === 2) {
      for (const s of [-1, 1]) {
        const q = at(len + hs * 0.5)
        out.push(part('frustum', [q[0] + side[0] * s * hs * 0.5, q[1], q[2] + side[2] * s * hs * 0.5], [hs * 0.25, hs * 1.2, hs * 0.25], { rot, taper: 0.3, step: 5, role: 'signature' }))
      }
    } else out.push(part('dish', tip, [hs * 1.6, hs * 0.4, hs * 1.6], { rot, step: 5, role: 'signature' }))
    // Strobe chain down the lane (step 5).
    const n = [4, 6, 8, 10][strI]
    for (let k = 1; k <= n; k++) {
      const q = at(len + hs + (k * 0.35 * H) / n)
      out.push(part('sphere', q, [60, 60, 60], { step: 5, role: 'accent' }))
    }
    mastCrown(out, kw * 0.3 * Math.sin(b), top, -kw * 0.3 * Math.cos(b), 0.2 * H, 5)
    mastCrown(out, -kw * 0.3 * Math.sin(b), top, kw * 0.3 * Math.cos(b), 0.2 * H, 5)
    return out
  },
}

/** A Meridian pod column on the y axis: tiers with collar necks; returns its tier tops for ports. */
function podColumn(out: MegaPart[], x: number, z: number, D0: number, tiers: number, th: number, profileI: number, y0: number): number[] {
  const tops: number[] = []
  let y = y0
  for (let i = 0; i < tiers; i++) {
    const t = tiers > 1 ? i / (tiers - 1) : 0
    let f: number
    if (profileI === 0) f = 1
    else if (profileI === 1) f = 0.75 + 0.35 * Math.sin(Math.PI * t)
    else if (profileI === 2) f = 1.1 - 0.45 * t
    else f = i % 2 === 0 ? 1.05 : 0.78
    const D = D0 * f
    const step = 1 + Math.min(3, Math.floor((4 * i) / tiers))
    out.push(part('podTier', [x, y + th * 0.42, z], [D, th * 0.84, D], { step }))
    out.push(part('drum', [x, y + th * 0.92, z], [D * 0.55, th * 0.16, D * 0.55], { step }))
    tops.push(y + th * 0.5)
    y += th
  }
  tops.push(y)
  return tops
}

// ── Trunk Line / Meridian ──────────────────────────────────────────────────────────────────────────────────────────
// Round 2: a marshalling yard, not a tower. A broad yard deck carries a hub column ringed by splayed satellite columns,
// joined by collar bridges; container-train ports stand out at stepped heights and the long train arm faces the gate.
export const trunkLineMeridian: FamilyGrammar = {
  kind: 'trunkLine',
  maker: 'meridian',
  slots: [
    { name: 'tiers', options: 4 }, // 6/8/10/12 hub tiers
    { name: 'satellites', options: 4 }, // 3/4/5/6 satellite columns
    { name: 'splay', options: 4 }, // upright pairs, leaning out, flaring wide, stepped spiral of heights
    { name: 'bridges', options: 4 }, // 1/2/3 collar-bridge levels, or alternating heights
    { name: 'yard', options: 4 }, // deck disc, stepped terraces, lobed pads, spoked rail yard
    { name: 'crown', options: 4 }, // dome, beacon collars, landing deck, collar spire
    { name: 'mainPort', options: 4, site: gateQuadrant }, // the long train arm faces the gate
    { name: 'collectors', options: 4, site: lumClass },
  ],
  build(g, site) {
    const [tierI, satI, splayI, bridgeI, yardI, crownI] = g.slots
    const p = g.props
    const H = 1900 + 600 * p[0]
    const out: MegaPart[] = []
    const gb = site.gateBearingRad
    const tiers = [6, 8, 10, 12][tierI]
    const th = H / (tiers + 1.5)
    const D0 = 0.4 * H * (0.9 + 0.2 * p[1])
    const Ds = D0 * 0.78
    const nS = satI + 3
    const R = (0.55 + 0.1 * p[2]) * H
    // The yard (step 1): a hub pad and rail spurs or pads out to every satellite; never one plain disc.
    const deckH = 0.07 * H
    const y0 = deckH
    out.push(part('podTier', [0, deckH / 2, 0], [D0 * (yardI === 1 ? 2.1 : 1.6), deckH, D0 * (yardI === 1 ? 2.1 : 1.6)]))
    if (yardI === 1) out.push(part('frustum', [0, deckH * 1.2, 0], [D0 * 1.4, deckH * 0.4, D0 * 1.4], { taper: 0.9 }))
    // The hub column (steps 1–4).
    const hubTops = podColumn(out, 0, 0, D0, tiers, th, Math.floor(p[3] * 4) % 4, y0)
    const hubTop = hubTops[hubTops.length - 1]
    // Satellite columns (steps 2–3), splayed round the hub, leaving the gate bearing clear for the train arm.
    const lean0 = [0, 0.16, 0.3, 0.1][splayI]
    const portRow = [0.55, 0.75, 0.4]
    const bridgeRows = bridgeI === 0 ? [0.55] : bridgeI === 1 ? [0.3, 0.75] : bridgeI === 2 ? [0.25, 0.5, 0.78] : [0.4]
    for (let i = 0; i < nS; i++) {
      const b = gb + Math.PI / nS + (i * TAU) / nS
      const hf = splayI === 0 ? (i % 2 === 0 ? 0.78 : 0.6) : splayI === 3 ? 0.4 + (0.5 * i) / Math.max(1, nS - 1) : 0.7 - 0.1 * (i % 2)
      const nt = Math.max(3, Math.round(tiers * hf))
      const lean = lean0
      const axis = (s: number): readonly [number, number] => [R + Math.sin(lean) * s, y0 + Math.cos(lean) * s]
      // Satellite pads and the rail spur in from each (step 1); the spoked yard adds chords between neighbours.
      out.push(radial('podTier', b, R, deckH / 2, [Ds * (yardI === 2 ? 1.9 : 1.4), deckH, Ds * (yardI === 2 ? 1.9 : 1.4)], { step: 1 }))
      if (yardI !== 2) out.push(radial('slab', b, R / 2, deckH * 0.5, [R, deckH * 0.8, Ds * 0.4], { step: 1, role: 'work' }))
      if (yardI === 3) {
        const chord = 2 * R * Math.sin(Math.PI / nS)
        out.push(radial('slab', b + Math.PI / nS, R * Math.cos(Math.PI / nS), deckH * 0.5, [Ds * 0.35, deckH * 0.8, chord], { step: 1, role: 'work' }))
      }
      for (let j = 0; j < nt; j++) {
        const [r, y] = axis((j + 0.42) * th)
        const f = j === nt - 1 ? 0.8 : j % 2 === 0 ? 1 : 0.9
        const step = j < nt / 2 ? 2 : 3
        out.push(radial('podTier', b, r, y, [Ds * f, th * 0.84, Ds * f], { rot: [0, 0, -lean], step }))
        const [rn, yn] = axis((j + 0.92) * th)
        out.push(radial('drum', b, rn, yn, [Ds * 0.55, th * 0.16, Ds * 0.55], { rot: [0, 0, -lean], step }))
      }
      // Collar bridges to the hub (step 3).
      const rows = bridgeI === 3 ? [i % 2 === 0 ? 0.35 : 0.7] : bridgeRows
      for (const row of rows) {
        const [r, y] = axis(row * nt * th)
        const len = r - D0 * 0.45
        out.push(radial('drum', b, D0 * 0.45 + len / 2, y, [len, th * 0.3, th * 0.3], { rot: [0, 0, Math.PI / 2], step: 3, role: 'work' }))
        out.push(radial('arc', b, D0 * 0.45 + len / 2, y, [th * 0.62, th * 0.62, 26], { rot: [0, Math.PI / 2, 0], arc: TAU, step: 3, role: 'accent' }))
      }
      // A container-train port at a stepped height (step 4): spar, collar rings, a docked train.
      const [rp, yp] = axis(portRow[i % 3] * nt * th)
      const armL = Ds * 0.7
      const r0 = rp + Ds * 0.45
      out.push(radial('podTier', b, r0 + armL / 2, yp, [armL, th * 0.5, th * 0.5], { rot: [0, 0, Math.PI / 2], step: 4, role: 'work' }))
      out.push(radial('arc', b, r0 + armL, yp, [th * 0.75, th * 0.75, 26], { rot: [0, Math.PI / 2, 0], arc: TAU, step: 4, role: 'accent' }))
      for (let k = 0; k < 2; k++) out.push(radial('slab', b, r0 + armL + th * (0.6 + 0.62 * k), yp, [th * 0.5, th * 0.3, th * 0.3], { step: 5, role: 'work' }))
    }
    // The main train arm toward the gate (step 4): a long port spar with lit collars and a departing train.
    const mainY = hubTops[Math.min(hubTops.length - 1, 2)]
    const mainL = R + 0.15 * H
    out.push(radial('podTier', gb, D0 * 0.5 + mainL / 2, mainY, [mainL, th * 0.62, th * 0.62], { rot: [0, 0, Math.PI / 2], step: 4, role: 'work' }))
    for (let k = 1; k <= 5; k++) {
      out.push(radial('arc', gb, D0 * 0.5 + (mainL * k) / 5, mainY, [th * 0.95, th * 0.95, 26], { rot: [0, Math.PI / 2, 0], arc: TAU, step: 4, role: 'accent' }))
    }
    for (let k = 0; k < 3; k++) out.push(radial('slab', gb, D0 * 0.5 + mainL + th * (0.7 + 0.7 * k), mainY, [th * 0.58, th * 0.34, th * 0.34], { step: 5, role: 'work' }))
    // Collector plates sized to the star (step 4), off the hub's flanks.
    const cs = lumScale(site) * 0.16 * H
    const cy = hubTops[Math.floor(hubTops.length * 0.7)]
    for (const s of [1, -1]) {
      out.push(radial('plate', gb + (s * Math.PI) / 2, D0 * 0.5 + cs * 0.5, cy, [cs, cs * 0.45, 14], { step: 4, role: 'accent' }))
    }
    // Crown (step 5).
    if (crownI === 0) out.push(part('sphere', [0, hubTop, 0], [D0 * 0.85, D0 * 0.6, D0 * 0.85], { step: 5 }))
    else if (crownI === 1) {
      for (let k = 0; k < 4; k++) out.push(part('arc', [0, hubTop + th * 0.5 * (k + 1), 0], [D0 * (0.7 - 0.12 * k), D0 * (0.7 - 0.12 * k), 30], { rot: [Math.PI / 2, 0, 0], arc: TAU, step: 5, role: 'accent' }))
      out.push(part('drum', [0, hubTop + th, 0], [D0 * 0.2, th * 2, D0 * 0.2], { step: 5 }))
    } else if (crownI === 2) {
      out.push(part('drum', [0, hubTop + th * 0.2, 0], [D0 * 0.3, th * 0.4, D0 * 0.3], { step: 5 }))
      out.push(part('drum', [0, hubTop + th * 0.5, 0], [D0 * 1.5, th * 0.18, D0 * 1.5], { step: 5 }))
    } else {
      for (let k = 0; k < 3; k++) out.push(part('podTier', [0, hubTop + th * (0.4 + 0.7 * k), 0], [D0 * (0.6 - 0.15 * k), th * 0.6, D0 * (0.6 - 0.15 * k)], { step: 5 }))
    }
    return out
  },
}

// ── Span / Meridian ────────────────────────────────────────────────────────────────────────────────────────────────
// Round 2: the colossal pod column leads (fatter pods, collar necks, a twin flanking column, a heavier pier); the
// runway of collar rings is smaller and shorter, the accent.
export const spanMeridian: FamilyGrammar = {
  kind: 'span',
  maker: 'meridian',
  slots: [
    { name: 'pose', options: 4 }, // laid flat, raised 12°, upright body + runway, raised on a pier
    { name: 'pods', options: 4 }, // 4/6/8/10 pods along the column
    { name: 'rings', options: 4 }, // 8/12/16/20 runway collars
    { name: 'runway', options: 4 }, // straight, converging, twin, spiralling
    { name: 'runLen', options: 3 },
    { name: 'tail', options: 4 }, // collector plates, tug pods, sphere tank, end collar
    { name: 'aim', options: 4, site: gateQuadrant }, // the runway points at the relay
    { name: 'collectors', options: 4, site: lumClass },
  ],
  build(g, site) {
    const [poseI, podI, ringI, runI, lenI, tailI] = g.slots
    const p = g.props
    const H = 1900 + 600 * p[0]
    const out: MegaPart[] = []
    const b = site.gateBearingRad
    const el = poseI === 1 ? 0.21 : 0
    const d: Vec3 = [Math.cos(b) * Math.cos(el), Math.sin(el), Math.sin(b) * Math.cos(el)]
    const side: Vec3 = [-Math.sin(b), 0, Math.cos(b)]
    const nP = [4, 6, 8, 10][podI]
    const D = 0.62 * H * (1.12 - 0.08 * podI) * (0.9 + 0.2 * p[1])
    const colL = (0.8 + 0.055 * nP) * H
    const pl = colL / nP
    let base: Vec3
    if (poseI === 2) {
      // Upright body: the column stands on a broad foot with a twin beside it; the runway leaves from its waist.
      out.push(part('frustum', [0, 0.04 * H, 0], [D * 1.9, 0.08 * H, D * 1.9], { taper: 0.8 }))
      const tops = podColumn(out, 0, 0, D, nP, pl * 0.9, 1, 0.08 * H)
      podColumn(out, side[0] * D * 0.85, side[2] * D * 0.85, D * 0.6, Math.max(3, nP - 2), pl * 0.9, 2, 0.08 * H)
      base = [0, tops[Math.floor(nP / 2)], 0]
    } else {
      const lift = poseI === 3 ? 0.42 * H : D * 0.55
      if (poseI === 3) {
        out.push(part('frustum', [0, lift * 0.45, 0], [D * 1.4, lift * 0.9, D * 1.1], { taper: 0.6 }))
        out.push(part('frustum', [0, lift * 0.06, 0], [D * 2.2, lift * 0.12, D * 1.8], { taper: 0.8 }))
      } else {
        out.push(part('frustum', [0, lift * 0.5, 0], [D * 2.2, lift, D * 1.3], { taper: 0.55 }))
      }
      base = [0, lift + D * 0.45, 0]
      for (let i = 0; i < nP; i++) {
        const t = (i - (nP - 1) / 2) * pl
        const f = i % 2 === 0 ? 1 : 0.86
        const q: Vec3 = [base[0] + d[0] * t, base[1] + d[1] * t, base[2] + d[2] * t]
        const step = 1 + Math.min(3, Math.floor((4 * i) / nP))
        out.push(part('podTier', q, [D * f, pl * 0.84, D * f], { rot: [0, -b, el - Math.PI / 2], step }))
        if (i > 0) {
          const qn: Vec3 = [q[0] - d[0] * pl * 0.5, q[1] - d[1] * pl * 0.5, q[2] - d[2] * pl * 0.5]
          out.push(part('drum', qn, [D * 0.6, pl * 0.2, D * 0.6], { rot: [0, -b, el - Math.PI / 2], step }))
        }
        // The flanking twin column, lower and shorter, laid along the main one (step 2).
        if (i > 0 && i < nP - 1) {
          const q2: Vec3 = [q[0] + side[0] * D * 0.62, q[1] - D * 0.22, q[2] + side[2] * D * 0.62]
          out.push(part('podTier', q2, [D * 0.55, pl * 0.8, D * 0.55], { rot: [0, -b, el - Math.PI / 2], step: 2 }))
        }
      }
      base = [base[0] + d[0] * colL * 0.5, base[1] + d[1] * colL * 0.5, base[2] + d[2] * colL * 0.5]
    }
    // The runway: countable lit collar rings marching toward the relay (steps 3–5).
    const nR = [8, 12, 16, 20][ringI]
    const runL = [0.5, 0.68, 0.86][lenI] * H
    const ringRot: Vec3 = [0, Math.PI / 2 - b, 0]
    const lanes = runI === 2 ? [-1, 1] : [0]
    for (const ln of lanes) {
      for (let k = 0; k < nR; k++) {
        const t = ((k + 1) / nR) * runL
        const sz = (runI === 1 ? 0.4 - 0.2 * (k / nR) : 0.3) * H
        const tw = runI === 3 ? Math.sin(k * 0.8) * 0.08 * H : 0
        const q: Vec3 = [base[0] + d[0] * t + side[0] * (ln * 0.15 * H + tw), base[1] + d[1] * t + (runI === 3 ? Math.cos(k * 0.8) * 0.08 * H : 0), base[2] + d[2] * t + side[2] * (ln * 0.15 * H + tw)]
        out.push(part('arc', q, [sz, sz, 30], { rot: ringRot, arc: TAU, step: 3 + Math.min(2, Math.floor((3 * k) / nR)), role: 'signature' }))
      }
    }
    // Tail (step 5), opposite the runway.
    const tail: Vec3 = poseI === 2 ? [-d[0] * D * 0.7, base[1] + 0.2 * H, -d[2] * D * 0.7] : [base[0] - d[0] * (colL + D * 0.3), base[1] - d[1] * colL, base[2] - d[2] * (colL + D * 0.3)]
    const cs = lumScale(site) * 0.26 * H
    if (tailI === 0) {
      out.push(part('plate', [tail[0] + side[0] * cs * 0.6, tail[1], tail[2] + side[2] * cs * 0.6], [cs, cs * 0.5, 16], { rot: [0, -b + Math.PI / 2, 0], step: 5, role: 'accent' }))
      out.push(part('plate', [tail[0] - side[0] * cs * 0.6, tail[1], tail[2] - side[2] * cs * 0.6], [cs, cs * 0.5, 16], { rot: [0, -b + Math.PI / 2, 0], step: 5, role: 'accent' }))
    } else if (tailI === 1) {
      const r = detailRng(g.detail, 31)
      for (let k = 0; k < 6; k++) out.push(part('podTier', [tail[0] + rf(r, -0.12, 0.12) * H, tail[1] + rf(r, -0.12, 0.12) * H, tail[2] + rf(r, -0.12, 0.12) * H], [0.1 * H, 0.14 * H, 0.1 * H], { step: 5 }))
    } else if (tailI === 2) {
      out.push(part('sphere', tail, [D * 1.1, D * 1.1, D * 1.1], { step: 5 }))
    } else {
      out.push(part('arc', tail, [D * 1.3, D * 1.3, 60], { rot: ringRot, arc: TAU, step: 5, role: 'accent' }))
      out.push(part('plate', [tail[0], tail[1] + cs * 0.4, tail[2]], [cs * 0.5, cs * 0.8, 16], { rot: [0, -b, 0], step: 5, role: 'accent' }))
    }
    return out
  },
}
